"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const ACTIVE = [
  {
    name: "Financial Summary",
    what: "Daily / weekly / monthly snapshot: sales, purchases, payments in/out, receivables, payables, net cash flow",
  },
  {
    name: "Top Performers",
    what: "Top customers, top items sold, top salesperson",
  },
  {
    name: "Daily Activity by User",
    what: "Who created which documents today",
  },
  {
    name: "Sales Pipeline & Conversion",
    what: "Quote → order → invoice funnel",
  },
  {
    name: "Quotations Summary",
    what: "Quotes added, by user, status, expiring soon",
  },
  {
    name: "Inventory Movement",
    what: "Stock in vs out, transfers/adjustments, top mover",
  },
];

const COMING_SOON = [
  "Receivables Aging",
  "Payables & Upcoming Dues",
  "Low Stock & Reorder Alerts",
  "Production Summary",
  "Pending & Drafts",
];

const RULES = [
  {
    topic: "Admin only, plan-gated",
    rule: "Settings → Organization → WhatsApp config. If the plan does not include it, the page shows WhatsApp config is not available on your plan. Active cards then show Upgrade plan (account billing) or Contact support on a custom plan — not Configure. You cannot switch catalog plans from a custom plan",
  },
  {
    topic: "Admin phones, not customers",
    rule: "Messages go to the Admin phone numbers you enter on each template. This is not WhatsApp to a customer's mobile from an invoice",
  },
  {
    topic: "Coming soon",
    rule: "Templates tagged Coming soon cannot be turned On — the card shows Coming soon instead of Configure. Only the six active templates can be configured",
  },
  {
    topic: "Phone limit",
    rule: "How many numbers you can add is the plan's WhatsApp recipients limit (unlimited on some plans). The modal tells you the cap",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. Active schedules" },
      { heading: "2. Configure a template" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function WhatsappConfigPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            WhatsApp Config
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins — scheduled summary WhatsApp to admin phones, not
            customer chat
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Title <B>WhatsApp config</B>. You turn templates On, pick Daily /
            Weekly / Monthly times, and list admin phone numbers. The product
            then sends those summaries on that schedule.
          </p>
          <Flow
            steps={[
              "Settings",
              "Organization",
              "WhatsApp config",
              "Configure",
              "Send this report On",
              "phones + schedule",
              "Save",
            ]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Active schedules
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The top of the page lists templates you have enabled (for example
            Daily at 07:30). If none are on: No templates enabled yet.
            Configure one below to start sending. Last summary sent / failed
            (with error) shows when a send has already run.
          </p>
          <p className="text-[16px] geist-medium text-[#111827] mb-2">
            Templates you can turn On
          </p>
          <Definitions
            items={ACTIVE.map((t) => ({ term: t.name, desc: t.what }))}
          />
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mt-4 mb-2">
            Tagged <B>Coming soon</B> (preview only, no Configure):{" "}
            {COMING_SOON.join(", ")}.
          </p>
          <Callout type="note">
            Card previews use sample numbers. Live messages use your books
            data when the job runs.
          </Callout>

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Configure a template
          </h3>
          <Steps
            items={[
              <>
                On an active template, click <B>Configure</B> (or{" "}
                <B>Edit schedule</B> when it is already On).
              </>,
              <>
                Turn <B>Send this report</B> on. Add <B>Admin phone numbers</B>{" "}
                (first required; Add phone number until the plan cap).
              </>,
              <>
                Enable <B>Daily</B> (time), <B>Weekly</B> (weekday + time),
                and/or <B>Monthly</B> (day of month + time). Save.
              </>,
            ]}
          />
          <Definitions
            items={[
              {
                term: "On / Off",
                desc: "Badge on the card. Off means that template does not send",
              },
              {
                term: "What it affects",
                desc: "Which WhatsApp summaries go to those admin numbers, and when. It does not message customers, and it does not post journals",
              },
            ]}
          />
          <Screenshot caption="WhatsApp config — active schedules, template cards On/Off, Configure modal with phones and Daily/Weekly/Monthly" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/settings/manage-subscription"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Manage Subscription <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Plan limits, including WhatsApp recipients
            </p>
          </Link>
          <Link
            href="/documentation/books/analytics/analytics-overview"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Analytics <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              On-screen rankings and lists — WhatsApp is the scheduled digest
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
